import type { RefObject } from 'react';

import { useIsomorphicLayoutEffect } from '@/shared/hooks/useIsomorphicLayoutEffect';

const traps: HTMLElement[] = [];

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Ловушка фокуса для диалогов.
 *
 * Помимо доступности это защита от реального бага в TMA: если фокус уходит на
 * элемент под оверлеем, Telegram WebView скроллит страницу к нему и sheet
 * визуально «уезжает».
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  active: boolean,
): void {
  useIsomorphicLayoutEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    traps.push(container);
    const isTopTrap = (): boolean => traps[traps.length - 1] === container;

    const focusFirst = (): void => {
      if (!isTopTrap() || !container.isConnected) return;
      const target = container.querySelector<HTMLElement>('[data-autofocus]') ?? container;
      target.focus({ preventScroll: true });
    };
    focusFirst();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab' || !isTopTrap()) return;
      const nodes = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (node) => node.offsetParent !== null || node === document.activeElement,
      );
      if (nodes.length === 0) {
        event.preventDefault();
        container.focus({ preventScroll: true });
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (!first || !last) return;

      const current = document.activeElement;
      if (current === container || !container.contains(current)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus({ preventScroll: true });
      } else if (event.shiftKey && current === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && current === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    };

    const onFocusIn = (event: FocusEvent): void => {
      if (isTopTrap() && event.target instanceof Node && !container.contains(event.target)) {
        focusFirst();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('focusin', onFocusIn, true);
    return () => {
      const wasTopTrap = isTopTrap();
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('focusin', onFocusIn, true);
      const index = traps.lastIndexOf(container);
      if (index !== -1) traps.splice(index, 1);
      // Closing a background dialog must not steal focus from a nested sheet.
      if (!wasTopTrap) return;
      const parent = traps[traps.length - 1];
      const target = previouslyFocused?.isConnected && (!parent || parent.contains(previouslyFocused))
        ? previouslyFocused
        : parent;
      target?.focus({ preventScroll: true });
    };
  }, [active, containerRef]);
}
